import React, { useState } from 'react';
import { Button, Divider, Input, message, Select, Space, Switch, Tabs } from 'antd';
import Title from 'antd/es/typography/Title';
import Sections from '../../Sections';
import TipTap from '../../Sections/TipTap';
import { v4 as uuidv4 } from 'uuid';
import { getDefaultRiskDriverContent } from '../../constants';

interface ContentItem {
  type: string; // content, table, or figure
  data: any;
  id?: string;
}

interface RiskDriver {
  id: string;
  title: string;
  summary: any;
  data: ContentItem[];
}

interface TopGHGRiskData {
  impact: { id: string; impact: string; description: string }[];
  description: any;
  showSummary: boolean;
  citations: any;
  riskDrivers: RiskDriver[];
}

interface TopGHGRisksProps {
  data: TopGHGRiskData;
  setData: (path: string, value: any) => void;
  activeTab: string;
  readonly?: boolean;
}

const TopGHGRisks: React.FC<TopGHGRisksProps> = ({
  data,
  setData,
  activeTab,
  readonly = false
}) => {
  const [showSummary, setShowSummary] = useState(data.showSummary);
  const sectionKey = `topGHGRisks.${activeTab}`;

  const handleRemoveImpact = (impactId: string) => {
    const updatedImpact = data.impact.filter((impact) => impact.id !== impactId);
    setData(`${sectionKey}.impact`, updatedImpact);
  };

  const riskDriverTabs = data.riskDrivers.map((riskDriver, index) => {
    return {
      label: `Risk Driver ${index + 1}`,
      key: `risk-driver-${riskDriver.id || index}`,
      children: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            placeholder="Risk Driver Title"
            value={riskDriver.title}
            onChange={(e) => {
              if (readonly) return;
              if (e.target.value.length > 100) {
                message.error('Title cannot exceed 100 characters');
                return;
              }
              const updatedRiskDrivers = data.riskDrivers.map((driver) => {
                if (driver.id === riskDriver.id) {
                  return { ...driver, title: e.target.value };
                }
                return driver;
              });
              setData(`${sectionKey}.riskDrivers`, updatedRiskDrivers);
            }}
            style={{ marginBottom: '10px' }}
            disabled={readonly}
            maxLength={100}
            showCount
          />
          {data.showSummary && (
            <>
              <Title level={5}>Short description of risk driver in table</Title>
              <TipTap
                value={riskDriver.summary}
                onChange={(value) => {
                  if (readonly) return;
                  const updatedRiskDrivers = data.riskDrivers.map((driver) => {
                    if (driver.id === riskDriver.id) {
                      return { ...driver, summary: value };
                    }
                    return driver;
                  });
                  setData(`${sectionKey}.riskDrivers`, updatedRiskDrivers);
                }}
                MAX_CHARS={550}
                readonly={readonly}
              />
            </>
          )}

          <Divider style={{ margin: '10px 0' }} />
          <Title level={5}>Risk driver discussion deep-dive</Title>
          <Sections
            data={data}
            setData={setData}
            sectionKey={sectionKey}
            readonly={readonly}
            dataKey="riskDrivers"
            containerId={riskDriver.id}
          />
        </Space>
      )
    };
  });

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space direction="horizontal" align="center" style={{ marginBottom: '10px' }}>
        <Title level={5} style={{ margin: 0 }}>
          Include in top risk drivers summary table
        </Title>
        <Switch
          checked={showSummary}
          onChange={(checked) => {
            if (readonly) return;
            setData(`${sectionKey}.showSummary`, checked);
            setShowSummary(checked);
          }}
          disabled={readonly}
        />
      </Space>
      <Title level={5}>Impact</Title>
      {data.impact.map((impact) => (
        <Space direction="horizontal" key={impact.id}>
          <Select
            value={impact.impact}
            onChange={(value) => {
              if (readonly) return;
              const updatedImpact = data.impact.map((impactItem) => {
                if (impactItem.id === impact.id) {
                  return { ...impactItem, impact: value };
                }
                return impactItem;
              });
              setData(`${sectionKey}.impact`, updatedImpact);
            }}
            style={{ width: 200 }}
            placeholder="Select impact level"
            disabled={readonly}>
            <Select.Option value="none">None</Select.Option>
            <Select.Option value="low">Low</Select.Option>
            <Select.Option value="moderate">Moderate</Select.Option>
            <Select.Option value="high">High</Select.Option>
            <Select.Option value="critical">Critical</Select.Option>
            <Select.Option value="variable">Variable</Select.Option>
          </Select>
          <Input
            value={impact.description}
            onChange={(e) => {
              if (readonly) return;
              const updatedImpact = data.impact.map((impactItem) => {
                if (impactItem.id === impact.id) {
                  return { ...impactItem, description: e.target.value };
                }
                return impactItem;
              });
              setData(`${sectionKey}.impact`, updatedImpact);
            }}
            placeholder="Qualifying text (optional) E.g. for flaring"
            style={{ width: 300 }}
            disabled={readonly}
          />
        </Space>
      ))}
      <Divider />
      <Title level={5} style={{ textTransform: 'capitalize' }}>
        {activeTab.toLowerCase()} introduction text
      </Title>
      <Sections
        data={data}
        setData={setData}
        sectionKey={sectionKey}
        readonly={readonly}
        dataKey="description"
      />
      <Divider />
      <Title level={5}>Citation list (enter as a numbered list)</Title>
      <TipTap
        value={data.citations}
        onChange={(value) => {
          if (readonly) return;
          setData(`${sectionKey}.citations`, value);
        }}
        readonly={readonly}
      />
      <Divider />
      <Tabs
        defaultActiveKey={
          data.riskDrivers.length ? `risk-driver-${data.riskDrivers[0].id}` : undefined
        }
        type={readonly ? 'card' : 'editable-card'}
        addIcon={<Button type="primary">Add Risk Driver</Button>}
        onEdit={
          readonly
            ? undefined
            : (targetKey, action) => {
                if (action === 'add') {
                  const newRiskDriver = {
                    title: '',
                    data: [...getDefaultRiskDriverContent()]
                  };
                  const updatedRiskDrivers = [...data.riskDrivers, newRiskDriver];
                  setData(`${sectionKey}.riskDrivers`, updatedRiskDrivers);
                } else if (action === 'remove') {
                  const updatedRiskDrivers = data.riskDrivers.filter(
                    (riskDriver) =>
                      riskDriver.id !== (targetKey as string).replace('risk-driver-', '')
                  );
                  setData(`${sectionKey}.riskDrivers`, updatedRiskDrivers);
                }
              }
        }
        items={riskDriverTabs}
      />
    </Space>
  );
};

export default TopGHGRisks;
